import { AuthContext } from './context/auth.context'
import type { DailyStockKpi } from '@gozoki/api-types'
import type { Right } from '@gozoki/tools'
import { useContext } from 'react'
import { useRbacV2Enabled } from './rbac'

/**
 * Droit v1 legacy (bitmask `adminRights`).
 *
 * Les deux régimes ne se cumulent jamais : dès que `permission-v2` est actif pour l'admin, le
 * bitmask n'a plus voix au chapitre — seuls les tuples v2 gardent l'UI, et un admin porteur des
 * bons droits v2 ne doit pas rester bloqué par un bitmask qu'il n'a pas. Tant que le flag est OFF,
 * le comportement legacy est strictement conservé.
 */
export const useRights = (rights: Right[]): boolean => {
  const { hasRights } = useContext(AuthContext)
  const v2Enabled = useRbacV2Enabled()

  return v2Enabled || hasRights(rights)
}

export const getProductList = (
  currentKpis: DailyStockKpi[] | undefined,
  currentProducts: number[],
  currentProductsRefs: string[],
  mapKey: keyof DailyStockKpi,
) => {
  if (!currentKpis) {
    return []
  }
  if (currentProducts.length > 0) {
    const result: { ref: string; count: number }[] = []
    currentKpis.forEach((kpi) => {
      Object.values(kpi[mapKey]).forEach(({ ref, count }) => {
        if (currentProductsRefs.includes(ref)) {
          result.push({ count, ref })
        }
      })
    })
    return result.reduce((acc: { ref: string; count: number }[], { ref, count }) => {
      const index = acc.findIndex((p) => p.ref === ref)
      if (index !== -1) {
        acc[index].count += count
      } else {
        acc.push({ count, ref })
      }
      return acc
    }, [])
  }
  return currentKpis
    .reduce((acc: { ref: string; count: number }[], kpi) => {
      acc.push(...(Object.values(kpi[mapKey]) as { ref: string; count: number }[]))
      return acc
    }, [])
    .reduce((acc: { ref: string; count: number }[], { ref, count }) => {
      const index = acc.findIndex((p) => p.ref === ref)
      if (index !== -1) {
        acc[index].count += count
      } else {
        acc.push({ count, ref })
      }
      return acc
    }, [])
}

export const getProductCount = (
  currentKpis: DailyStockKpi[] | undefined,
  currentProductsRefs: string[],
  mapKey: keyof DailyStockKpi,
) => {
  if (!currentKpis) {
    return 0
  }
  if (currentProductsRefs.length > 0) {
    let totalCount = 0
    currentKpis?.forEach((kpi) => {
      Object.values(kpi[mapKey]).forEach(({ ref, count }) => {
        if (currentProductsRefs.includes(ref)) {
          totalCount += count
        }
      })
    })
    return totalCount
  }
  let totalCount = 0
  currentKpis?.forEach((kpi) => {
    Object.values(kpi[mapKey]).forEach(({ count }) => {
      totalCount += count
    })
  })
  return totalCount
}
